import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';

import { ToastService } from '@plugins/client/toast/toast.service';
import { TranslateService } from '@plugins/client/translate/services/translate.service';
import { InstanceDto } from '@plugins/octacornerstone-lib/instance';
import {
  LearningObjectChildrenResponse,
  UserData,
  UserLearningDataDto,
} from '@plugins/octacornerstone-lib/types';

import { Training } from '../frontend/shared/models/training';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  completedTrainings: BehaviorSubject<Training[] | null>;
  activeTrainings: BehaviorSubject<Training[] | null>;
  selectedTraining = new BehaviorSubject<Training | null>(null);
  user = new BehaviorSubject<UserData | null>(null);

  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  public instance!: InstanceDto;

  fetchInstance(): Observable<InstanceDto> {
    return this.http.get<InstanceDto>('/api/instance').pipe(
      retry({ delay: 7000 }),
      tap((instance) => (this.instance = instance)),
    );
  }

  updateInstance(instance: Partial<InstanceDto>): Observable<InstanceDto> {
    return this.http
      .patch<InstanceDto>('/api/instance', { instance })
      .pipe(tap((updated) => (this.instance = updated)));
  }

  getActiveTrainings() {
    if (!this.activeTrainings?.getValue()) {
      this.activeTrainings = new BehaviorSubject<Training[] | null>(null);

      return this.getTrainings('active', this.activeTrainings);
    }

    return this.activeTrainings.asObservable();
  }

  getCompletedTrainings() {
    if (!this.completedTrainings?.getValue()) {
      this.completedTrainings = new BehaviorSubject<Training[] | null>(null);

      return this.getTrainings('completed', this.completedTrainings);
    }

    return this.completedTrainings.asObservable();
  }

  getCurriculumChildren(training: Training): Observable<Training> {
    return this.http
      .get<LearningObjectChildrenResponse>(
        '/api/learning-objects/details/' + training.learningObjectId,
      )
      .pipe(
        map((response) => {
          const subject = training.isCompleted
            ? this.completedTrainings
            : this.activeTrainings;
          const trainings = subject.getValue();
          const index = trainings.findIndex(
            (t) => t.learningObjectId === training.learningObjectId,
          );
          if (index === -1) {
            console.error('Training not found in the cache');
            return;
          }

          const trainingWithChildren = new Training({
            ...training,
            children: response.data,
          });
          trainings.splice(index, 1, trainingWithChildren);

          subject.next(trainings);
          return trainingWithChildren;
        }),
      );
  }

  private getTrainings(
    status: 'completed' | 'active',
    subject: BehaviorSubject<Training[]>,
  ) {
    return this.http
      .get<UserLearningDataDto>('/api/learning-objects/' + status)
      .pipe(
        switchMap((data) => {
          const mappedData = data.learningObjects.map((lo) => new Training(lo));
          subject.next(mappedData);
          this.user.next(data.userData);
          return subject.asObservable();
        }),
        catchError(() => {
          this.toastService.show({
            message: this.translateService.instant('error-loading-trainings'),
            color: 'red',
            closeTimeout: 5000,
          });
          return subject.asObservable();
        }),
      );
  }
}
